import { Button, Flex, SegmentedControl, Tooltip } from "@mantine/core";
import { IconMailForward } from "@tabler/icons-react";
import React from "react";
import { useSelector } from "react-redux";
import { PartnerAdminApi } from "../../apis";
import { IAdminCompany } from "../../interfaces/IAdminCompany";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../models/General";
import { Dialog } from "../../utils/confirm-modal";
import { dateSecFormat } from "../../utils/date";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";

function PartnerManagementAdminCompanyList(): React.JSX.Element {
  const [filters, setFilters] = React.useState({ partnerType: "COMPANY", userStatus: "ACTIVE" });
  const { departmentUnits, userStatus, userRegisterStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const ref = React.useRef<ITableRef>(null);

  const columns = useHeader({
    userRegisterStatus,
    userStatus,
    departmentUnits,
    onClick: (key, _item) => {
      switch (key) {
        case "edit": {
          break;
        }
        case "details": {
          // navigation(`/dehub/partner/${_item.id}`);
          break;
        }
        case "remove": {
          Dialog.confirm("Та үүнийг устгахдаа итгэлтэй байна уу?", async (key) => {
            switch (key) {
              case "confirm": {
                try {
                  // await PartnerApi.remove(`${item?.id}`);
                  reload();
                  Message.success("Ажилтан амжилттай устгагдлаа");
                } catch (err: any) {
                  Message.error((err as HttpHandler)?.message!);
                }
                break;
              }
              default:
            }
          });
          break;
        }
      }
    },
  });

  const reload = async () => {
    ref.current?.reload();
  };

  return (
    <>
      <SegmentedControl
        onChange={(e: string) => {
          setFilters({ ...filters, userStatus: e });
        }}
        value={filters.userStatus}
        data={userStatus?.map((item: IReference) => {
          return {
            label: item.name,
            value: item?.code,
          };
        })}
        radius="md"
        size="md"
        mb="md"
      />
      <Table ref={ref} name="partner-management-admin-company.list" columns={columns} filters={filters} loadData={(data) => PartnerAdminApi.list(data!)} />
    </>
  );
}

export { PartnerManagementAdminCompanyList };

type HeaderProps = {
  onClick: (key: string, record: IAdminCompany) => void;
  departmentUnits: IReference[];
  userStatus: IReference[];
  userRegisterStatus: IReference[];
};

const useHeader = ({ onClick, userRegisterStatus, userStatus }: HeaderProps): ColumnType<IAdminCompany>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Tooltip label="Урилга илгээх" position="top">
            <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("send", record)}>
              <IconMailForward />
            </Button>
          </Tooltip>
        </Flex>
      );
    },
  },
  {
    title: "Хэрэглэгчийн нэр",
    sorter: true,
    dataIndex: "username",
    render: (record) => <RenderText text={record?.username || "-"} />,
  },
  {
    title: "DeHUB менежер эсэх",
    sorter: true,
    dataIndex: "isDehubManager",
    render: (record) => <Tag color={record.isDehubManager ? "green.5" : "red.5"} name={record.isDehubManager ? "Тийм" : "Үгүй"} />,
  },
  {
    title: "DeHUB санхүү эсэх",
    sorter: true,
    dataIndex: "isDehubFinance",
    render: (record) => <Tag color={record.isDehubFinance ? "green.5" : "red.5"} name={record.isDehubFinance ? "Тийм" : "Үгүй"} />,
  },
  {
    title: "Ургийн овог",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => <RenderText text={record?.familyName || "-"} />,
  },
  {
    title: "Эцэг эхийн нэр",
    sorter: true,
    dataIndex: "lastName",
    render: (record) => <RenderText text={record?.lastName || "-"} />,
  },
  {
    title: "Өөрийн нэр",
    sorter: true,
    dataIndex: "firstName",
    render: (record) => <RenderText text={record?.firstName || "-"} />,
  },
  {
    title: "Регистр №",
    sorter: true,
    dataIndex: "registerNo",
    render: (record) => <RenderText text={record?.registerNo || "-"} />,
  },
  {
    title: "Нэгжийн нэр",
    sorter: true,
    dataIndex: "departmentUnit",
    render: (record) => <RenderText text={record?.departmentUnitId || "-"} />,
  },
  {
    title: "Албан тушаал",
    sorter: true,
    dataIndex: "employeeUnit",
    render: (record) => <RenderText text={record?.employeeUnitId || "-"} />,
  },
  {
    title: "И-мэйл хаяг",
    sorter: true,
    dataIndex: "email",
    render: (record) => <RenderText text={record?.email || "-"} />,
  },
  {
    title: "Утас 1",
    sorter: true,
    dataIndex: "phone",
    render: (record) => <RenderText text={record?.phone || "-"} />,
  },
  {
    title: "Утас 2",
    sorter: true,
    dataIndex: "phone2",
    render: (record) => <RenderText text={record?.phone2 || "-"} />,
  },
  {
    title: "Бүртгэсэн огноо",
    sorter: true,
    dataIndex: "createdAt",
    render: (record) => <RenderText text={dateSecFormat(record?.createdAt)} />,
  },
  {
    title: "Хэрэглэгчийн статус",
    sorter: true,
    dataIndex: "itemStatus",
    render: (record) => (
      <Tag
        color={userStatus.find((item: IReference) => item.code === record.userStatus)?.color || "gray"}
        name={userStatus.find((item: IReference) => item.code === record.userStatus)?.name || "Идэвхгүй"}
      />
    ),
  },
  {
    title: "Статус",
    sorter: true,
    dataIndex: "itemStatus",
    render: (record) => (
      <Tag
        color={userRegisterStatus?.find((item: IReference) => item.code === record.registerStatus)?.color || "gray"}
        name={userRegisterStatus?.find((item: IReference) => item.code === record.registerStatus)?.name || "Амжилтгүй"}
      />
    ),
  },
  {
    title: "Утас баталгаажсан эсэх",
    sorter: true,
    dataIndex: "isPhoneVerified",
    render: (record) => <Tag color={record.isPhoneVerified ? "green.5" : "red.5"} name={record.isPhoneVerified ? "Тийм" : "Үгүй"} />,
  },
  {
    title: "Имэйл баталгаажсан эсэх",
    sorter: true,
    dataIndex: "isEmailVerified",
    render: (record) => <Tag color={record.isEmailVerified ? "green.5" : "red.5"} name={record.isEmailVerified ? "Тийм" : "Үгүй"} />,
  },
  {
    title: "Нууц үгтэй эсэх",
    sorter: true,
    dataIndex: "hasPassword",
    render: (record) => <Tag color={record.hasPassword ? "green.5" : "red.5"} name={record.hasPassword ? "Тийм" : "Үгүй"} />,
  },
  {
    title: "ДАН - р баталгаажсан эсэх",
    sorter: true,
    dataIndex: "isDanVerified",
    render: (record) => <Tag color={record.isDanVerified ? "green.5" : "red.5"} name={record.isDanVerified ? "Тийм" : "Үгүй"} />,
  },
];
